import React, { useState, useEffect } from 'react';
import apiClient from '../../utils/api';
import { getUserPermissions } from '../../utils/auth';
import { useTranslation } from '../../i18n';

const ModelsTab = () => {
  const { t } = useTranslation();
  const [selectedModel, setSelectedModel] = useState('');
  const [availableModels, setAvailableModels] = useState([]);
  const [systemPrompt, setSystemPrompt] = useState({
    content: '',
    isCustom: false,
    loading: false
  });
  const [useCustomPrompt, setUseCustomPrompt] = useState(false);
  const [permissions, setPermissions] = useState({
    can_edit_prompt: false,
    can_change_model: false
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadSystemPrompt();
    loadPermissions();
    loadModels();
  }, []);

  const loadPermissions = async () => {
    const perms = await getUserPermissions();
    setPermissions(perms);
  };

  const loadModels = async () => {
    try {
      const response = await apiClient.get('/bedrock/models');
      setAvailableModels(response.data.models || []);
    } catch (error) {
      console.error('Error loading models:', error);
    }
  };

  const loadSystemPrompt = async () => {
    try {
      const response = await apiClient.get('/user/preferences');
      console.log('Loaded preferences:', response.data);
      setSelectedModel(response.data.preferences?.model || '');
      setSystemPrompt({
        content: response.data.system_prompt || '',
        isCustom: response.data.is_custom_prompt || false,
        loading: false
      });
      setUseCustomPrompt(response.data.is_custom_prompt || false);
      console.log('System prompt state:', {
        content: response.data.system_prompt,
        isCustom: response.data.is_custom_prompt,
        useCustomPrompt: response.data.is_custom_prompt
      });
    } catch (error) {
      console.error('Error loading system prompt:', error);
    }
  };

  const handleSavePrompt = async () => {
    setLoading(true);
    setSuccess('');

    try {
      const payload = {
        system_prompt: useCustomPrompt ? systemPrompt.content : null
      };
      
      if (selectedModel) {
        payload.preferences = { model: selectedModel };
      }
      
      console.log('Saving payload:', payload);
      await apiClient.post('/user/preferences', payload);
      setSuccess('Configuration saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving configuration:', error);
      console.error('Error response:', error.response?.data);
      alert(`Failed to save: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPrompt = async () => {
    if (!window.confirm('Reset to default system prompt?')) return;

    setLoading(true);
    try {
      await apiClient.post('/user/preferences', {
        system_prompt: null
      });
      await loadSystemPrompt();
      setUseCustomPrompt(false);
      setSuccess('Reset to default prompt');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error resetting prompt:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!permissions.can_edit_prompt) {
    return (
      <div className="tab-content">
        <div className="tab-header">
          <h2>{t('aiModels')}</h2>
          <p>{t('aiModelsDesc')}</p>
        </div>
        <div className="alert alert-info">
          <p>🔒 {t('advancedAIConfigRequires')}</p>
          <p>{t('contactAdminToUpgrade')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tab-content">
      <div className="tab-header">
        <h2>{t('aiModels')}</h2>
        <p>{t('configureAIBehavior')}</p>
      </div>

      {success && <div className="alert alert-success">{success}</div>}

      <div className="models-section">
        <div className="form-group">
          <label>{t('aiModel')}</label>
          <select
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value)}
            disabled={!permissions.can_change_model}
          >
            <option value="">{t('selectModel')}</option>
            {availableModels.map(model => (
              <option key={model.id} value={model.id}>
                {model.name} ({model.provider})
              </option>
            ))}
          </select>
          <small>{t('chooseAIModel')}</small>
        </div>

        <div className="form-group">
          <div className="checkbox-group">
            <input
              type="checkbox"
              id="useCustomPrompt"
              checked={useCustomPrompt}
              onChange={(e) => setUseCustomPrompt(e.target.checked)}
            />
            <label htmlFor="useCustomPrompt">
              {t('useCustomPrompt')}
            </label>
          </div>
          <small>{t('overrideDefaultAI')}</small>
        </div>

        {useCustomPrompt && (
          <div className="form-group">
            <label>{t('customSystemPrompt')}</label>
            <textarea
              value={systemPrompt.content}
              onChange={(e) => setSystemPrompt({ ...systemPrompt, content: e.target.value })}
              rows={10}
              placeholder={t('enterCustomPrompt')}
              className="prompt-textarea"
            />
            <small>{t('defineAIBehavior')}</small>
          </div>
        )}

        {!useCustomPrompt && (
          <div className="info-box">
            <p>{t('usingDefaultPrompt')}</p>
          </div>
        )}

        <div className="button-group">
          <button 
            className="btn btn-primary" 
            onClick={handleSavePrompt}
            disabled={loading}
          >
            {loading ? t('saving') : t('saveConfiguration')}
          </button>
          
          {systemPrompt.isCustom && (
            <button 
              className="btn btn-outline-secondary" 
              onClick={handleResetPrompt}
              disabled={loading}
            >
              Reset to Default
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModelsTab;
